<template>
	<div class="flex xs-12">
		<div class="xs-7 flex wrap">
			<div class="flex xs-12 p4 xs-between">
				<!-- Actions -->
				<template-page-navigator
					:page="page"
					@goToPage="goToPage"
					:numPages="numPages"
					@zoomReset="resetZoom"
					@zoomIn="zoomIn"
					@zoomOut="zoomOut"
					@onPreview="showPreviewModal"
				/>
			</div>

			<div class="xs-12 pt8 pb8 p2 bg4 flex xs-center">
				<div class="pdf">
					<canvas class="viewer pr4 pb4" ref="pdfViewer" />

					<template-editor
						class="fabric pr4 pb4"
						v-if="editorIsReady"
						v-bind="{
							page,
							height,
							width,
							drawings: drawingsForPage,
							scale,
							activeTool,
							activeObject
						}"
						@addDrawing="addDrawing"
						@updateDrawing="updateDrawing"
						@savePdfDrawing="savePdfDrawing"
						@removeDrawing="removeDrawing"
						@editLiquid="editLiquid"
						@selectObject="selectObject"
					/>
				</div>
			</div>
		</div>

		<div class="flex xs-5">
			<template-drawing-detail
				:activeObject="activeObject"
				:drawings="drawings"
				@updated:pdfDrawing="$emit('updated:pdfDrawing')"
				@removed:pdfDrawing="$emit('removed:pdfDrawing', $event)"
				@duplicated:pdfDrawing="$emit('addPdfDrawing', { ...$event, page })"
			/>
			<!-- <template-drawing-list
				:page="this.page"
				v-if="editorIsReady"
				v-bind="{ drawings, activeObject, selectObject }"
				@removeDrawing="removeDrawing"
				@editLiquid="editLiquid"
				@selectObject="selectObject"
				@duplicateDrawing="duplicateDrawing"
			/>-->
		</div>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon"
import CoreModal from "@/modules/core/components/CoreModal"
import JBaseInputSearch from "@/components/JBaseInputSearch"
import JBaseInputNumber from "@/components/JBaseInputNumber"
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputLongText from "@/components/JBaseInputLongText"
import JButton from "@/components/JButton"

import TemplateDrawingList from "./TemplateDrawingList"
import TemplateDrawingDetail from "./TemplateDrawingDetail"
import TemplateEditor from "./TemplateEditor"
import TemplatePageNavigator from "./TemplatePageNavigator"

import JSpinner from "@/components/JSpinner"
import JBaseSelect from "@/components/JBaseSelect"

import api from "../../api"
import fontList from "./fontList"

import pdfjs from "pdfjs-dist"
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry"

export default {
	components: {
		CoreModal,
		CrmIcon,
		JBaseInputLongText,
		JBaseInputNumber,
		JBaseInputText,
		JBaseInputSearch,
		JBaseSelect,
		JButton,
		JBaseSelect,
		JSpinner,
		TemplateDrawingList,
		TemplateDrawingDetail,
		TemplateEditor,
		TemplatePageNavigator
	},
	props: {
		source: {
			type: String
		},
		drawings: {
			type: Array
		},
		templateId: {
			type: String
		}
	},
	data: function() {
		return {
			pdf: undefined, // pdf file
			scale: 1,
			increment: 0.25,
			maxZoom: 3,

			page: 1,
			height: undefined,
			width: undefined,

			numPages: undefined,

			editorIsReady: false,
			activeTool: "edit",
			currentView: "layers",

			activeObject: null,
			liquidSource: null,

			fontFamily: null,
			fontSize: null,

			isLoading: false,

			pageRendering: false,
			pageNumPending: null
		}
	},

	computed: {
		fontList() {
			return fontList.fonts.map(font => {
				return {
					value: font,
					name: font
				}
			})
		},
		defaultFontFamily() {
			return this.activeObject
				? this.activeObject?.font?.familyName
				: this.fontFamily
		},
		fontSizes() {
			let output = []
			for (let i = 1; i < 64; i++) {
				output.push({
					value: i.toString(),
					name: i.toString()
				})
			}
			return output
		},
		defaultFontSize() {
			return this.activeObject
				? this.activeObject?.font?.emSize?.toString()
				: this.fontSize
		},

		xActiveObj() {
			return parseInt(this.activeObject?.point1?.x) || 0.0
		},
		yActiveObj() {
			return parseInt(this.activeObject?.point1?.y) || 0.0
		},
		widthActiveObj() {
			return this.activeObject?.point1 && this.activeObject?.point2
				? parseInt(this.activeObject.point2.x - this.activeObject.point1.x)
				: 0
		},
		heightActiveObj() {
			return this.activeObject?.point1 && this.activeObject?.point2
				? parseInt(this.activeObject.point2.y - this.activeObject.point1.y)
				: 0
		},

		drawingsForPage() {
			return this.drawings.filter(d => d.page === this.page)
		},

		horizontalAlignmentIcons() {
			return ["left", "center", "right"]
		},
		verticalAlignmentIcons() {
			return ["top", "center", "bottom"]
		},
		textAlignment() {
			return this.activeObject?.textAlignment?.toLowerCase()
		},
		verticalAlignment() {
			return this.activeObject?.verticalAlignment?.toLowerCase()
		}
	},

	methods: {
		async renderPage(pageNumber) {
			this.pageRendering = true

			await this.pdf.getPage(pageNumber).then(async page => {
				const viewport = page.getViewport({ scale: this.scale })
				const pdfViewer = this.$refs.pdfViewer

				pdfViewer.height = viewport.height
				pdfViewer.width = viewport.width

				this.height = pdfViewer.height
				this.width = pdfViewer.width

				this.editorIsReady = false

				page
					.render({
						canvasContext: pdfViewer.getContext("2d"),
						viewport
					})
					.promise.then(() => {
						this.editorIsReady = true // instantiate editor

						this.pageRendering = false
						if (this.pageNumPending !== null) {
							// New page rendering is pending
							this.renderPage(this.pageNumPending)
							this.pageNumPending = null
						}
					})
					.catch(error => {
						if (error.name === "RenderingCancelledException") {
							this.renderPage(pageNumber)
							return
						}
					})
			})
		},

		queueRenderPage(num) {
			if (this.pageRendering) {
				this.pageNumPending = num
			} else {
				this.renderPage(num)
			}
		},

		// render
		async showPreviewModal() {
			this.setActiveTool("preview")
			this.$modal.show("preview_template")
		},

		goToPage(value) {
			if (this.activeObject?.page !== value) {
				this.activeObject = null
			}
			this.page = value
			this.queueRenderPage(value)
		},

		removeAll() {
			this.$emit("removeAll")
		},

		view(view) {
			this.currentView = view
		},

		setActiveTool(item) {
			this.activeTool = item
		},

		selectObject(item) {
			this.activeObject = item
		},

		editLiquid(item) {
			this.activeObject = item
			this.liquidSource = item.liquidSource
			this.$modal.show("update_template")
			this.setActiveTool("liquid")
		},

		// drawing helpers
		addDrawing(drawing) {
			this.$emit("addPdfDrawing", { ...drawing, page: this.page })
		},

		generateDrawingObject(options, page) {
			const { target } = options
			const { id, left, top } = target

			const height = target.height * this.scale
			const width = target.width * this.scale

			let pointX1 = left / this.scale
			let pointY1 = top / this.scale

			let pointX2 = (left + (width * target.scaleX) / this.scale) / this.scale
			let pointY2 = (top + (height * target.scaleY) / this.scale) / this.scale

			return {
				id,
				hasRotatingPoint: false,
				width,
				height,
				point1: {
					x: pointX1,
					y: pointY1
				},
				point2: {
					x: pointX2,
					y: pointY2
				},
				page
			}
		},

		savePdfDrawing(options) {
			const data = this.generateDrawingObject(options, this.page)
			this.$emit("savePdfDrawing", {
				...data,
				verticalAlignment: this.activeObject?.verticalAlignment || "CENTER",
				textAlignment: this.activeObject?.textAlignment || "LEFT"
			})
		},

		updateDrawing(options) {
			const data = this.generateDrawingObject(options, this.page)
			this.$emit("updateDrawing", data)
		},

		// filtering and removing the target id
		removeDrawing(target) {
			this.liquidSource = null
			this.activeObject = null

			this.$emit("removePdfDrawing", target.id)
		},

		// zoom helpers
		zoomIn() {
			if (this.scale >= this.maxZoom) return
			this.updateScale(this.scale + this.increment)
		},

		zoomOut() {
			if (this.scale <= this.increment) return
			this.updateScale(this.scale - this.increment)
		},

		resetZoom() {
			this.updateScale(1)
		},

		updateScale(value) {
			this.scale = value
			this.renderPage(this.page)
		},

		handleKeypress(ev) {
			// remove active object
			if (this.activeTool === "liquid") {
				return
			}

			// if (this.activeTool === "edit" && ev.which === 8 && this.activeObject) {
			// 	this.removeDrawing(this.activeObject)
			// }
		}
	},

	async created() {
		pdfjs.disableFontFace = false
		pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker

		await pdfjs
			.getDocument({
				url: this.source,
				httpHeaders: { Authorization: `Bearer ${window.TK}` }
			})
			.promise.then(pdf => {
				this.pdf = pdf
				this.numPages = pdf.numPages

				this.renderPage(this.page)
			})
			.then(() => {
				this.editorIsReady = true
			})
			.catch(error => {
				if (error.name === "InvalidPDFException") {
					// try replacing pdfjs with inline object or as image
					this.$emit("error", {
						type: "error",
						message: this.$t("browser_not_supported")
					})
					return
				}
				// here error
				this.$emit("error", error)
			})
	},

	mounted() {
		window.addEventListener("keyup", this.handleKeypress)
	},
	beforeDestroy() {
		window.removeEventListener("keyup", this.handleKeypress)
		this.$store.commit("templates/SET_GRAPHQL_VARIABLES", "")
	},
	watch: {
		drawings: function(newVal, oldVal) {}
	}
}
</script>

<style scoped>
.pdf {
	width: 800px;
	height: 800px;
	position: relative;
	overflow: auto;
}

.fabric {
	position: absolute;
	left: 0;
	top: 0;
}

.pdfSpinner {
	position: absolute;
	left: 0;
	top: 0;
}
</style>
